import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Chip,
  Grid,
  Paper,
  Fab,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Tag as TagIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { makeApiCall } from '../../global/apiCall';

const colorOptions = [
  '#10B981', '#3B82F6', '#F59E0B', '#EF4444', 
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
  '#6B7280', '#059669', '#7C3AED', '#DC2626'
];

const TagsManager = ({ open, onClose, onTagSelect, selectedTags = [] }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, tag: null });
  const [formData, setFormData] = useState({
    name: '',
    color: '#10B981'
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchTags();
    }
  }, [open]);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await makeApiCall('/tags', 'GET');
      console.log('Full tags API response:', response);
      
      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          console.log('Tags data is array:', response.data);
          setTags(response.data);
          setError(''); // Clear any previous error
        } else {
          console.error('Tags response.data is not an array:', response.data);
          setTags([]);
          setError('Tags data format is incorrect');
        }
      } else {
        console.error('Tags API call failed:', response);
        // Fallback to mock data if API fails
        console.log('Using fallback mock tags data');
        const mockTags = [
          {
            id: '1',
            name: 'Important',
            color: '#EF4444',
            isSystem: false,
            usageCount: 0
          },
          {
            id: '2',
            name: 'Client',
            color: '#3B82F6',
            isSystem: false,
            usageCount: 0
          },
          {
            id: '3',
            name: 'Follow-up',
            color: '#F59E0B',
            isSystem: false,
            usageCount: 0
          },
          {
            id: '4',
            name: 'VIP',
            color: '#8B5CF6',
            isSystem: false,
            usageCount: 0
          }
        ];
        setTags(mockTags);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
      // Fallback to mock data on error
      console.log('Using fallback mock tags data due to error');
      const mockTags = [
        {
          id: '1',
          name: 'Important',
          color: '#EF4444',
          isSystem: false,
          usageCount: 0
        },
        {
          id: '2',
          name: 'Client',
          color: '#3B82F6',
          isSystem: false,
          usageCount: 0
        },
        {
          id: '3',
          name: 'Follow-up',
          color: '#F59E0B',
          isSystem: false,
          usageCount: 0
        },
        {
          id: '4',
          name: 'VIP',
          color: '#8B5CF6',
          isSystem: false,
          usageCount: 0
        }
      ];
      setTags(mockTags);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = () => {
    setFormData({
      name: '',
      color: '#10B981'
    });
    setEditDialog({ open: true, tag: null });
  };

  const handleEditTag = (tag) => {
    setFormData({
      name: tag.name,
      color: tag.color
    });
    setEditDialog({ open: true, tag });
  };

  const handleSaveTag = async () => {
    if (!formData.name.trim()) {
      setError('Tag name is required');
      return;
    }

    setLoading(true);
    try {
      const method = editDialog.tag ? 'PUT' : 'POST';
      const url = editDialog.tag ? `/tags/${editDialog.tag.id}` : '/tags';
      
      const response = await makeApiCall(url, method, formData);
      
      if (response.success) {
        setEditDialog({ open: false, tag: null });
        fetchTags();
        setError('');
      } else {
        // Mock save for now
        console.log('Mocking tag save:', formData);
        if (editDialog.tag) {
          // Update existing tag
          setTags(prev => prev.map(t => t.id === editDialog.tag.id ? { ...t, ...formData } : t));
        } else {
          // Add new tag
          const newTag = {
            id: Date.now().toString(),
            ...formData,
            isSystem: false,
            usageCount: 0
          };
          setTags(prev => [...prev, newTag]);
        }
        setEditDialog({ open: false, tag: null });
        setError('');
      }
    } catch (err) {
      // Mock save for now on error
      console.log('Mocking tag save due to error:', formData);
      if (editDialog.tag) {
        // Update existing tag
        setTags(prev => prev.map(t => t.id === editDialog.tag.id ? { ...t, ...formData } : t));
      } else {
        // Add new tag
        const newTag = {
          id: Date.now().toString(),
          ...formData,
          isSystem: false,
          usageCount: 0
        };
        setTags(prev => [...prev, newTag]);
      }
      setEditDialog({ open: false, tag: null });
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (tag.isSystem) {
      setError('Cannot delete system tag');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${tag.name}"? This tag will be removed from all cards.`)) {
      setLoading(true);
      try {
        const response = await makeApiCall(`/tags/${tag.id}`, 'DELETE');
        
        if (response.success) {
          fetchTags();
          setError('');
        } else {
          // Mock delete for now
          console.log('Mocking tag delete:', tag.id);
          setTags(prev => prev.filter(t => t.id !== tag.id));
          setError('');
        }
      } catch (err) {
        // Mock delete for now on error
        console.log('Mocking tag delete due to error:', tag.id);
        setTags(prev => prev.filter(t => t.id !== tag.id));
        setError('');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTagClick = (tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    let newSelection;
    
    if (isSelected) {
      newSelection = selectedTags.filter(t => t.id !== tag.id);
    } else {
      newSelection = [...selectedTags, tag];
    }
    
    onTagSelect(newSelection);
  };

  const isTagSelected = (tag) => {
    return selectedTags.some(t => t.id === tag.id);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Manage Tags
          <Fab
            size="small"
            color="primary"
            onClick={handleCreateTag}
            sx={{ ml: 2 }}
          >
            <AddIcon />
          </Fab>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Click tags to select/deselect them. Selected tags will be highlighted.
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Array.isArray(tags) && tags.map((tag) => (
                <Paper
                  key={tag.id}
                  elevation={isTagSelected(tag) ? 3 : 1}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    border: isTagSelected(tag) ? 2 : 1,
                    borderColor: isTagSelected(tag) ? 'primary.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                      elevation: 3,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => handleTagClick(tag)}
                >
                  <LocalOfferIcon 
                    sx={{ 
                      color: tag.color, 
                      fontSize: 20 
                    }} 
                  />
                  
                  <Typography variant="body2" sx={{ fontWeight: isTagSelected(tag) ? 600 : 400 }}>
                    {tag.name}
                  </Typography>
                  
                  {tag.isSystem && (
                    <Chip
                      label="System"
                      size="small"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  )}
                  
                  <Typography variant="caption" color="text.secondary">
                    ({tag.usageCount || 0})
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTag(tag);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    
                    {!tag.isSystem && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTag(tag);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
            
            {Array.isArray(tags) && tags.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TagIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No tags yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first tag to organize your business cards
                </Typography>
              </Box>
            )}
          </Stack>
          
          {selectedTags.length > 0 && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Tags ({selectedTags.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    onDelete={() => handleTagClick(tag)}
                    sx={{ 
                      bgcolor: tag.color + '20',
                      color: tag.color,
                      '& .MuiChip-deleteIcon': {
                        color: tag.color,
                      }
                    }}
                  />
                ))}
              </Box>
            </Paper>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Tag Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, tag: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialog.tag ? 'Edit Tag' : 'Create New Tag'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Tag Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {colorOptions.map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '2px solid #fff',
                    boxShadow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setFormData({ ...formData, color })}
                >
                  {formData.color === color && (
                    <LocalOfferIcon sx={{ color: '#fff', fontSize: 20 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, tag: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveTag} variant="contained" disabled={loading}>
            {editDialog.tag ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TagsManager;